interface CadenceLogoProps {
  className?: string
}

/**
 * Cadence brand logo icon with solid background.
 * Uses the dark blue rounded square background with white/colored strokes.
 * Ideal for favicons, app icons, and standalone branding.
 */
export function CadenceLogoSolid({ className = 'w-7 h-7' }: CadenceLogoProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 512 512"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M420.978 0H91.0222C40.7521 0 0 40.7521 0 91.0222V420.978C0 471.248 40.7521 512 91.0222 512H420.978C471.248 512 512 471.248 512 420.978V91.0222C512 40.7521 471.248 0 420.978 0Z"
        fill="#192A42"
      />
      <path
        d="M356.551 155.449C334.404 133.28 305.559 119.045 274.491 114.955C243.423 110.866 211.875 117.152 184.747 132.836"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M142.222 170.667H142.364"
        stroke="#87CFE9"
        strokeWidth="48"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M117.902 222.151C112.57 243.869 112.435 266.539 117.507 288.319C122.58 310.098 132.717 330.376 147.095 347.506C161.474 364.632 179.689 378.129 200.262 386.896C220.834 395.665 243.185 399.457 265.498 397.966C287.812 396.476 309.458 389.746 328.684 378.322C347.907 366.896 364.166 351.098 376.141 332.211C388.116 313.324 395.466 291.877 397.596 269.617C399.73 247.356 396.581 224.905 388.409 204.089"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M316.302 195.698C308.119 187.465 298.34 180.99 287.565 176.666C276.792 172.343 265.249 170.264 253.644 170.556C242.039 170.847 230.616 173.504 220.073 178.362C209.53 183.221 200.089 190.18 192.328 198.813C184.567 207.446 178.649 217.571 174.937 228.57C171.224 239.569 169.794 251.21 170.735 262.781C171.677 274.351 174.968 285.608 180.41 295.862C185.852 306.116 193.328 315.153 202.382 322.418"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M256 341.333H256.142"
        stroke="#7BC97B"
        strokeWidth="48"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M341.191 251.165C341.965 264.695 339.504 278.215 334.014 290.606C328.525 302.996 320.159 313.902 309.618 322.418"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M276.053 235.947L356.551 155.449"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M256 284.445C271.709 284.445 284.445 271.709 284.445 256C284.445 240.291 271.709 227.556 256 227.556C240.291 227.556 227.556 240.291 227.556 256C227.556 271.709 240.291 284.445 256 284.445Z"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Cadence brand logo icon (white strokes only).
 * For use on colored/dark backgrounds where white strokes are needed.
 */
export function CadenceLogoWhite({ className = 'w-7 h-7' }: CadenceLogoProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 310 310"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M255.551 54.4489C233.404 32.2796 204.559 18.0446 173.491 13.9554C142.423 9.86624 110.875 16.1516 83.7466 31.8356"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M41.2222 69.6667H41.3644"
        stroke="#87CFE9"
        strokeWidth="48"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.9022 121.151C11.5703 142.869 11.4349 165.539 16.5071 187.319C21.5796 209.098 31.7167 229.376 46.0953 246.506C60.474 263.632 78.689 277.129 99.2617 285.896C119.834 294.665 142.185 298.457 164.498 296.966C186.812 295.476 208.459 288.746 227.684 277.322C246.907 265.896 263.166 250.098 275.141 231.211C287.116 212.324 294.466 190.877 296.596 168.617C298.73 146.356 295.581 123.905 287.409 103.089"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M215.302 94.6978C207.119 86.4654 197.34 79.9897 186.565 75.6664C175.792 71.3435 164.249 69.2642 152.644 69.5557C141.039 69.8473 129.616 72.5037 119.073 77.3623C108.53 82.2209 99.089 89.1796 91.328 97.8127C83.5672 106.446 77.649 116.571 73.9365 127.57C70.2236 138.569 68.794 150.21 69.7352 161.781C70.6765 173.351 73.9683 184.608 79.41 194.862C84.8515 205.116 92.3281 214.153 101.382 221.418"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M155 240.333H155.142"
        stroke="#7BC97B"
        strokeWidth="48"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M240.191 150.165C240.965 163.695 238.504 177.215 233.015 189.606C227.525 201.996 219.159 212.902 208.618 221.418"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M175.053 134.947L255.551 54.449"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M155 183.445C170.709 183.445 183.444 170.709 183.444 155C183.444 139.291 170.709 126.556 155 126.556C139.291 126.556 126.556 139.291 126.556 155C126.556 170.709 139.291 183.445 155 183.445Z"
        stroke="white"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Cadence brand logo icon.
 * Automatically switches between light and dark variants based on theme.
 * - Light mode: dark blue strokes on transparent background
 * - Dark mode: white strokes on transparent background
 */
export function CadenceLogo({ className = 'w-7 h-7' }: CadenceLogoProps) {
  return (
    <>
      {/* Light mode version (dark strokes) */}
      <svg
        className={`${className} dark:hidden`}
        viewBox="0 0 310 310"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M255.551 54.4489C233.404 32.2796 204.559 18.0446 173.491 13.9554C142.423 9.86624 110.875 16.1516 83.7467 31.8356"
          stroke="#192A42"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M41.2222 69.6667H41.3644"
          stroke="#87CFE9"
          strokeWidth="48"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.9022 121.151C11.5703 142.869 11.4349 165.539 16.5071 187.319C21.5796 209.098 31.7166 229.376 46.0953 246.506C60.4739 263.632 78.6889 277.129 99.2616 285.896C119.834 294.665 142.185 298.457 164.498 296.966C186.812 295.476 208.458 288.746 227.684 277.322C246.907 265.896 263.166 250.098 275.141 231.211C287.116 212.324 294.466 190.877 296.596 168.617C298.73 146.356 295.581 123.905 287.409 103.089"
          stroke="#192A42"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M215.302 94.6978C207.119 86.4654 197.34 79.9897 186.565 75.6664C175.792 71.3435 164.249 69.2642 152.644 69.5557C141.039 69.8473 129.616 72.5037 119.073 77.3623C108.53 82.2209 99.0891 89.1796 91.328 97.8127C83.5673 106.446 77.6491 116.571 73.9365 127.57C70.2237 138.569 68.7941 150.21 69.7353 161.781C70.6765 173.351 73.9684 184.608 79.4101 194.862C84.8515 205.116 92.3281 214.153 101.382 221.418"
          stroke="#192A42"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M155 240.333H155.142"
          stroke="#7BC97B"
          strokeWidth="48"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M240.191 150.165C240.965 163.695 238.504 177.215 233.015 189.606C227.525 201.996 219.159 212.902 208.618 221.418"
          stroke="#192A42"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M175.053 134.947L255.551 54.449"
          stroke="#192A42"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M155 183.445C170.709 183.445 183.444 170.709 183.444 155C183.444 139.291 170.709 126.556 155 126.556C139.291 126.556 126.556 139.291 126.556 155C126.556 170.709 139.291 183.445 155 183.445Z"
          stroke="#192A42"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {/* Dark mode version (white strokes) */}
      <svg
        className={`${className} hidden dark:block`}
        viewBox="0 0 310 310"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M255.551 54.4489C233.404 32.2796 204.559 18.0446 173.491 13.9554C142.423 9.86624 110.875 16.1516 83.7466 31.8356"
          stroke="white"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M41.2222 69.6667H41.3644"
          stroke="#87CFE9"
          strokeWidth="48"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.9022 121.151C11.5703 142.869 11.4349 165.539 16.5071 187.319C21.5796 209.098 31.7167 229.376 46.0953 246.506C60.474 263.632 78.689 277.129 99.2617 285.896C119.834 294.665 142.185 298.457 164.498 296.966C186.812 295.476 208.459 288.746 227.684 277.322C246.907 265.896 263.166 250.098 275.141 231.211C287.116 212.324 294.466 190.877 296.596 168.617C298.73 146.356 295.581 123.905 287.409 103.089"
          stroke="white"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M215.302 94.6978C207.119 86.4654 197.34 79.9897 186.565 75.6664C175.792 71.3435 164.249 69.2642 152.644 69.5557C141.039 69.8473 129.616 72.5037 119.073 77.3623C108.53 82.2209 99.089 89.1796 91.328 97.8127C83.5672 106.446 77.649 116.571 73.9365 127.57C70.2236 138.569 68.794 150.21 69.7352 161.781C70.6765 173.351 73.9683 184.608 79.41 194.862C84.8515 205.116 92.3281 214.153 101.382 221.418"
          stroke="white"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M155 240.333H155.142"
          stroke="#7BC97B"
          strokeWidth="48"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M240.191 150.165C240.965 163.695 238.504 177.215 233.015 189.606C227.525 201.996 219.159 212.902 208.618 221.418"
          stroke="white"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M175.053 134.947L255.551 54.449"
          stroke="white"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M155 183.445C170.709 183.445 183.444 170.709 183.444 155C183.444 139.291 170.709 126.556 155 126.556C139.291 126.556 126.556 139.291 126.556 155C126.556 170.709 139.291 183.445 155 183.445Z"
          stroke="white"
          strokeWidth="24"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </>
  )
}
